import type { ReferralType } from './referral';

/**
 * Defines how a field should be displayed in the UI
 */
export enum DisplayType {
  CHECKBOX = 'checkbox',
  DROPDOWN = 'dropdown',
  BUTTONS = 'buttons',
}

/**
 * Type for diagnosis option configuration with display type
 */
export interface DiagnosisOption {
  name: string;
  displayType: DisplayType;
  options: string[];
}

/**
 * Type for options in a dropdown
 */
export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Type for boolean question configuration
 */
export interface BooleanQuestion {
  id: string;
  text: string;
}

/**
 * Type for date field configuration
 */
export interface DateField {
  id: string;
  label: string;
  required?: boolean;
}

/**
 * Configuration type for specialty-specific referral forms
 */
export interface SpecialtyFormConfig {
  specialty: string;
  displayName: string;
  referralTypes: ReferralType[];
  diagnosisOptions: DiagnosisOption[];
  requiresToothSelection: boolean;
  booleanQuestions?: BooleanQuestion[]; // Optional array of boolean questions
  requiresEmail?: boolean; // Whether email is required for this specialty
  dateFields?: DateField[]; // Optional array of date fields
  treatmentQuestions?: DiagnosisOption[]; // Optional dropdown questions rendered after Diagnosis Selection
  /** Overrides the flat-list reason selector heading (e.g. "Choose a Medical Reason"). */
  reasonSelectorHeading?: string;
}

/**
 * Temporary storage type for specialty-specific data
 * Used in Phase 1 before proper database schema changes
 */
export interface SpecialtyData {
  diagnosis: string[];
  booleanAnswers?: Record<string, boolean | undefined>;
  dateValues?: Record<string, string | null>;
}

/**
 * Enum for dental specialties
 * Aligned with SPECIALTIES in api.constants.ts
 */
export const DentalSpecialty = {
  ENDODONTIC: 'Endodontist',
  ORAL_SURGERY: 'Oral Surgeon',
  PERIODONTAL: 'Periodontist',
  ORTHODONTIC: 'Orthodontist',
  TMJ: 'TMJ Specialist',
  SLEEP: 'Sleep Apnea',
  PEDIATRICS: 'Pediatrics',
  RADIOLOGY: 'Oral Radiologist',
  HOSPITAL: 'Hospital Dentist',
  PATHOLOGY: 'Oral Pathology',
  PROSTHODONTIC: 'Prosthodontist',
  GENERAL_DENTISTRY: 'General Dentistry',
} as const;

export type DentalSpecialty = (typeof DentalSpecialty)[keyof typeof DentalSpecialty];

/**
 * Set of permitted DentalSpecialty values, for O(1) validation of user-supplied
 * specialty arrays. Shared across the team-member create/update/bulk-update
 * endpoints to avoid triplicated copies (#1225).
 */
export const PERMITTED_SPECIALTY_VALUES = new Set<string>(Object.values(DentalSpecialty));
